﻿import { useMutation } from "@tanstack/react-query";
import { useToast } from "@chakra-ui/react";
import { AxiosError } from "axios";
import { axiosInstance } from "../../utils";

export interface UploadFileRequestArgs {
    tenantId: string | undefined;
    file: File;
}

function getApiErrorMessage(error: AxiosError) {
    const responseData = error.response?.data;

    if (responseData && typeof responseData === "object") {
        const problemDetails = responseData as {
            detail?: string;
            errors?: Record<string, string[] | string>;
        };

        if (problemDetails.detail) {
            return problemDetails.detail;
        }

        if (problemDetails.errors && typeof problemDetails.errors === "object") {
            const firstError = Object.values(problemDetails.errors)
                .flatMap(value => (Array.isArray(value) ? value : [value]))
                .find(message => typeof message === "string" && message.length > 0);

            if (firstError) {
                return firstError;
            }
        }
    }

    return `[${error.request?.status ?? "ERR"}]: ${error.message}`;
}

export function useWageImportJobCreate() {
    const axios = axiosInstance();
    const toast = useToast();

    return useMutation({
        mutationFn: (request: UploadFileRequestArgs) => {
            const formData = new FormData();
            formData.append("file", request.file);

            return axios.post(`api/clients/${request.tenantId}/wagejobs`, formData).then(response => {
                return response.data;
            });
        },
        onSuccess: async () => {
            toast({
                title: "Payroll file uploaded successfully.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });
        },
        onError: error => {
            const axiosError = error as AxiosError;
            toast({
                title: "There was an error importing the file.",
                description: getApiErrorMessage(axiosError),
                status: "error",
                isClosable: true,
                duration: null,
            });
        },
    });
}
